// ────────────────────────────────────────────────────────────────────────────
// Route info — the journey this order actually has to make, laid out
// horizontally between the references and the tasks.
//
// Answers two things an order screen otherwise cannot:
//   • does this need a sea crossing, is one booked, and by when must it be?
//   • where is the WAITING, and what would remove it?
//
// The second is the reason it exists. A driver landing at 11:00 for a delivery
// booked at 15:00 is four hours of nothing, and neither the task list nor the
// booking panel shows it: the tasks each look fine on their own, and the
// booking only knows about the crossing. It only shows up when the whole chain
// is added up, which is what `journeyPlan` does.
//
// Deliberately dense. It sits above the tasks on a screen that already has
// references, documents, tasks and charges to get through, so every row here is
// a row of those pushed further down — one header line, one timeline, and a
// line per thing worth acting on.
// ────────────────────────────────────────────────────────────────────────────
import type { OrderEmissions } from '@my/tms-api';
import { cn } from '@my/ui';
import { AlertTriangle, ChevronRight, Leaf, Ship, TriangleAlert, Truck } from 'lucide-react';
import {
  dayIndexOf,
  dayLabel,
  fmtClock,
  fmtSpan,
  type JourneyPlan,
  type PlanPoint,
  type PlanSegment,
  type PlanSuggestion,
} from '../lib/journeyPlan';

/** Leg styling — a wait is the one an operator should notice. */
const SEGMENT_STYLE: Record<PlanSegment['kind'], { label: string; tone: string }> = {
  road: { label: 'road', tone: 'text-slate-500' },
  checkIn: { label: 'check-in', tone: 'text-slate-400' },
  crossing: { label: 'crossing', tone: 'text-sky-600' },
  customs: { label: 'customs', tone: 'text-violet-600' },
  driveOff: { label: 'drive-off', tone: 'text-slate-400' },
  wait: { label: 'wait', tone: 'text-amber-600 font-medium' },
  // Not amber. The load sitting at its own depot until it needs to leave is the
  // plan working, not a problem to flag.
  hold: { label: 'held at depot', tone: 'text-slate-500' },
};

const VERDICT_CHIP: Record<JourneyPlan['verdict'], string> = {
  onTime: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  tight: 'bg-amber-50 text-amber-800 ring-amber-200',
  late: 'bg-red-50 text-red-800 ring-red-200',
  unknown: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const VERDICT_LABEL: Record<JourneyPlan['verdict'], string> = {
  onTime: 'On time',
  tight: 'Tight',
  late: 'Will not make it',
  unknown: 'Not enough detail',
};

export interface RouteInfoPanelProps {
  plan: JourneyPlan;
  /** Whether a booking already exists for the crossing. */
  booked: boolean;
  /**
   * Applies a suggestion. Absent while the wiring is unbuilt, in which case the
   * suggestion still reads as advice rather than pretending to be a button.
   */
  onApply?: (suggestion: PlanSuggestion) => void;
  /** The order's stored CO2 estimate; null while loading or with no journey. */
  emissions?: OrderEmissions | null;
}

const kg = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

/** The hover breakdown: each leg, what it counted, and how the figure was reached. */
function emissionsTitle(e: OrderEmissions): string {
  const legs = e.legs.map((l) => {
    const ends = `${l.from ?? '?'} → ${l.to ?? '?'}`;
    if (l.kind === 'Crossing') return `${ends}: ${l.routeName ?? 'sea crossing'} (not counted)`;
    if (l.kilometres == null) return `${ends}: no location`;
    return `${ends}: ${l.estimated ? '~' : ''}${l.kilometres} km`;
  });
  return [
    ...legs,
    '',
    `${e.ladenKm} km laden × ${e.factorKgPerKm} kg/km = ${kg.format(e.co2Kg)} kg CO₂`,
    e.method,
    ...(e.complete ? [] : ['~ part of the distance is a straight-line estimate']),
    ...(e.unlocatedStops > 0 ? [`${e.unlocatedStops} stop(s) have no location`] : []),
    ...(e.stale ? ['Out of date: the order changed and it could not be recalculated yet'] : []),
  ].join('\n');
}

/** "Est. CO₂ ~X kg · Y km laden · temperature-controlled" — the header's last item. */
function EmissionsLine({ emissions: e }: { emissions: OrderEmissions }) {
  const approx = !e.complete || e.stale ? '~' : '';
  return (
    <span
      className={cn(
        'inline-flex cursor-help items-center gap-1 whitespace-nowrap text-[11px]',
        e.stale ? 'text-slate-400' : 'text-slate-600',
      )}
      title={emissionsTitle(e)}
    >
      <Leaf className="h-3 w-3 text-emerald-600" />
      Est. CO₂{' '}
      <span className="font-medium text-slate-800">
        {approx}
        {kg.format(e.co2Kg)} kg
      </span>
      <span className="text-slate-400">· {kg.format(e.ladenKm)} km laden</span>
      {e.temperatureControlled && <span className="text-slate-400">· temperature-controlled</span>}
    </span>
  );
}

/**
 * A time, dated when the date matters.
 *
 * <p>
 * `showDay` is decided by the caller rather than by "is this past midnight".
 * Every step of the journey carries its own date, so no node's day has to be
 * inferred from the node before it — an overnight crossing means a reader
 * otherwise has to work out which side of midnight a port sits on. A DEADLINE
 * is dated only when it differs from the arrival it is being compared against,
 * since repeating the date it already sits next to adds nothing.
 * </p>
 */
function Stamp({
  at,
  baseDate,
  showDay,
  className,
}: {
  at: number | null;
  baseDate: string;
  showDay: boolean;
  className?: string;
}) {
  if (at == null) return <span className="font-mono text-slate-400">—</span>;
  return (
    <span className={cn('font-mono', className ?? 'text-slate-600')}>
      {showDay && <span className="mr-0.5 font-sans text-slate-500">{dayLabel(baseDate, at)}</span>}
      {fmtClock(at)}
    </span>
  );
}

/** One stop or port: label above, time below. Two lines, so the row stays thin. */
function Node({ point, baseDate }: { point: PlanPoint; baseDate: string }) {
  const day = point.atMinutes == null ? null : dayIndexOf(point.atMinutes);
  const dueDay = point.dueMinutes == null ? null : dayIndexOf(point.dueMinutes);
  return (
    <div className="min-w-[5rem] max-w-[10rem] shrink-0">
      <p
        className={cn(
          'flex items-center gap-1 truncate text-[11px] leading-4',
          point.kind === 'port' ? 'text-sky-700' : 'font-medium text-slate-800',
        )}
        title={point.label}
      >
        {point.kind === 'port' && <Ship className="h-3 w-3 shrink-0 text-sky-500" />}
        <span className="truncate">{point.label}</span>
      </p>
      <p className="whitespace-nowrap text-[10px] leading-4">
        <Stamp at={point.atMinutes} baseDate={baseDate} showDay />
        {point.dueMinutes != null && (
          <span className="ml-1 text-slate-400">
            due{' '}
            <Stamp
              at={point.dueMinutes}
              baseDate={baseDate}
              showDay={dueDay !== day}
              className="text-slate-400"
            />
          </span>
        )}
      </p>
    </div>
  );
}

/**
 * The legs between two nodes: their durations on one line, the connector under
 * it. Several legs (check-in, waiting, the crossing) share the line rather than
 * stacking, which is what keeps a crossing from tripling the panel's height.
 */
function Connector({
  legs,
  unbooked,
}: {
  legs: PlanSegment[];
  unbooked: boolean;
}) {
  const crossing = legs.some((l) => l.kind === 'crossing');
  return (
    <div className="min-w-[4.5rem] shrink-0 grow px-1">
      <p className="flex items-center justify-center gap-1 whitespace-nowrap text-[10px] leading-4">
        {legs.length === 0 ? (
          <span className="text-slate-300">—</span>
        ) : (
          legs.map((leg, i) => (
            <span key={`${leg.kind}-${i}`} className={SEGMENT_STYLE[leg.kind].tone}>
              {i > 0 && <span className="mr-1 text-slate-300">·</span>}
              {/* Spans, not hours: a three-day hold reads as "3d 11h", not
                  "83h 26m". Identical to `fmtDuration` under a day. */}
              {fmtSpan(leg.minutes)}
              {/* A tilde is the whole disclosure for an estimated leg —
                  spelling it out on each one would drown the numbers. */}
              {leg.estimated && '~'} {SEGMENT_STYLE[leg.kind].label}
            </span>
          ))
        )}
        {/* The sign that this boat is a proposal, not a booking. It belongs on
            the crossing itself: the header says a crossing is needed, this says
            nobody has arranged one. */}
        {crossing && unbooked && (
          <span className="rounded bg-amber-100 px-1 text-[9px] font-medium uppercase tracking-wide text-amber-800">
            not booked
          </span>
        )}
      </p>
      <div className="flex items-center leading-4">
        <span
          className={cn(
            'h-px flex-1',
            legs.some((l) => l.kind === 'wait') ? 'bg-amber-300' : 'bg-slate-200',
          )}
        />
        <ChevronRight className="h-3 w-3 shrink-0 text-slate-300" />
      </div>
    </div>
  );
}

export function RouteInfoPanel({ plan, booked, onApply, emissions }: RouteInfoPanelProps) {
  const unbooked = plan.needsCrossing && !booked;

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      {/* One line: what kind of journey, by when it must be booked, and whether
          it works at all. */}
      <div className="flex items-center gap-x-2 border-b border-slate-100 px-3 py-1">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Route info
        </h3>

        {plan.needsCrossing ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
            <Ship className="h-3 w-3 text-sky-600" />
            Sea crossing
            {plan.countryChange && (
              <span className="font-mono text-[10px] text-slate-500">
                {plan.countryChange.from}→{plan.countryChange.to}
              </span>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
            <Truck className="h-3 w-3 text-slate-400" />
            Road only
          </span>
        )}

        {emissions && <EmissionsLine emissions={emissions} />}

        {/* The number an operator books against, and the reason the backwards
            calculation exists. Only meaningful with a crossing and a deadline. */}
        {/* The one number a planner acts on: when the truck has to set off. Only
            exists on a backwards-planned order, which is the case it matters
            for — with a sailing booked, the port deadline below is the driver's
            constraint instead. */}
        {plan.latestLeaveCollectionMinutes != null && (
          <span className="ml-auto whitespace-nowrap text-[11px] text-slate-600">
            Leave by{' '}
            <Stamp
              at={plan.latestLeaveCollectionMinutes}
              baseDate={plan.baseDate}
              showDay
              className="font-medium text-slate-800"
            />
          </span>
        )}

        {plan.latestIntoPortMinutes != null && (
          <span
            className={cn(
              'whitespace-nowrap text-[11px] text-slate-600',
              plan.latestLeaveCollectionMinutes == null && 'ml-auto',
            )}
          >
            {unbooked ? 'Book a sailing by' : 'Latest into port'}{' '}
            <Stamp
              at={plan.latestIntoPortMinutes}
              baseDate={plan.baseDate}
              showDay={
                plan.latestLeaveCollectionMinutes == null ||
                dayIndexOf(plan.latestIntoPortMinutes) !==
                  dayIndexOf(plan.latestLeaveCollectionMinutes)
              }
              className="font-medium text-slate-800"
            />
            {plan.latestDepartureMinutes != null && (
              <span className="text-slate-400">
                {' '}
                · sails{' '}
                <Stamp
                  at={plan.latestDepartureMinutes}
                  baseDate={plan.baseDate}
                  showDay={
                    dayIndexOf(plan.latestDepartureMinutes) !==
                    dayIndexOf(plan.latestIntoPortMinutes)
                  }
                  className="text-slate-400"
                />
              </span>
            )}
          </span>
        )}

        <span
          className={cn(
            'whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ring-1',
            plan.latestIntoPortMinutes == null && 'ml-auto',
            VERDICT_CHIP[plan.verdict],
          )}
        >
          {VERDICT_LABEL[plan.verdict]}
          {plan.etaMinutes != null && (
            <>
              {' · ETA '}
              {dayIndexOf(plan.etaMinutes) > 0 && `${dayLabel(plan.baseDate, plan.etaMinutes)} `}
              {fmtClock(plan.etaMinutes)}
            </>
          )}
        </span>
      </div>

      {/* The timeline. Scrolls horizontally rather than wrapping: a journey read
          out of order is worse than one that needs a swipe. */}
      <div className="flex items-center gap-1 overflow-x-auto px-3 py-1.5">
        {plan.points.map((point, i) => (
          <div key={`${point.label}-${i}`} className="flex min-w-max grow items-center gap-1">
            {i > 0 && <Connector legs={point.legs} unbooked={unbooked} />}
            <Node point={point} baseDate={plan.baseDate} />
          </div>
        ))}
        {plan.hasEstimates && (
          <span
            className="ml-1 shrink-0 self-end whitespace-nowrap text-[9px] text-slate-300"
            title="Road times are straight-line estimates until truck routing is wired up"
          >
            ~ est.
          </span>
        )}
      </div>

      {/* Advice, not automation — each line names the saving so an operator can
          judge whether it is worth the phone call. */}
      {plan.suggestions.length > 0 && (
        <ul className="border-t border-slate-100">
          {plan.suggestions.map((s) => (
            <li key={s.kind} className="flex items-center gap-1.5 px-3 py-1">
              {s.kind === 'cannotMakeIt' || s.kind === 'cannotReachSailing' ? (
                <TriangleAlert className="h-3 w-3 shrink-0 text-red-500" />
              ) : (
                <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
              )}
              <p className="flex-1 text-[11px] leading-4 text-slate-600">{s.message}</p>
              {onApply && s.kind === 'moveDeliveryEarlier' && s.toMinutes != null && (
                <button
                  type="button"
                  onClick={() => onApply(s)}
                  className="shrink-0 rounded border border-slate-200 bg-white px-1.5 text-[10px] font-medium text-sky-700 hover:bg-sky-50"
                >
                  Set {fmtClock(s.toMinutes)}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
